import React from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CircularProgress from '@material-ui/core/CircularProgress';
import ConfigurationManager from '../../configuration/ConfigurationManager';

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        padding: 0,
        fontSize: 15,
        height: 'auto'
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
    },
    chip: {
        margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08,
        ),
    },
    noOptionsMessage: {
        padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
    },
    singleValue: {
        fontSize: 12.5,
        backgroundColor: '#dfe6e9',
        paddingTop: 2.5,
        paddingBottom: 2.5,
        paddingRight: 7.5,
        paddingLeft: 7.5,
        borderRadius: 10,
        marginBottom: 5
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: 26,
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    divider: {
        height: theme.spacing.unit * 2,
    },
});

function NoOptionsMessage(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.noOptionsMessage}
            {...props.innerProps}
        >
            No results found
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }) {
    return <div ref={inputRef} {...props} />;
}

function Control(props) {
    return (
        <TextField
            fullWidth
            InputProps={{
                inputComponent,
                inputProps: {
                    className: props.selectProps.classes.input,
                    inputRef: props.innerRef,
                    children: props.children,
                    ...props.innerProps,
                },
            }}
            {...props.selectProps.textFieldProps}
        />
    );
}

// function Option(props) {
//     return (
//         <MenuItem
//             buttonRef={props.innerRef}
//             selected={props.isFocused}
//             component="div"
//             style={{
//                 fontWeight: props.isSelected ? 500 : 400,
//             }}
//             {...props.innerProps}
//         >
//             {props.children}
//         </MenuItem>
//     );
// }

function Placeholder(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.placeholder}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function SingleValue(props) {
    return (
        <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function ValueContainer(props) {
    return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props) {
    return (
        <Chip
            style={{
                height: '24px',
                fontSize: 12.5,
            }}
            tabIndex={-1}
            label={props.children}
            className={classNames(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused,
            })}
            onDelete={props.removeProps.onClick}
            size="small"
            deleteIcon={<CancelIcon
                {...props.removeProps} />}
        />
    );
}

function Menu(props) {
    return (
        <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
            {props.children}
        </Paper>
    );
}

const DropdownIndicator = props => {
    return (
        <components.DropdownIndicator {...props}>
            <ArrowDropDownIcon
                color={'action'}
            />
        </components.DropdownIndicator>
    );
};

const LoadingIndicator = props => {
    return (
        <CircularProgress size={20} />
    );
};

const losComponentes = {
    Control,
    Menu,
    MultiValue,
    Placeholder,
    SingleValue,
    ValueContainer,
    IndicatorSeparator: () => null,
    DropdownIndicator,
    LoadingIndicator,
    NoOptionsMessage
};

class Dropdown extends React.Component {

    state = {
        isMounted: true,
        data: this.props.data || [],
        inputValue: '',
    };

    UNSAFE_componentWillReceiveProps({ data, loading }) {
        this.setState({ ...this.state.data, data })
        this.setState({ ...this.state.isLoading, loading })
    }

    componentDidMount() {
        this.setState({ isMounted: false })
    }

    loadOptions = (inputValue, callback) => {
        setTimeout(() => {
            callback(this.filterData(inputValue));
        }, 4000)
    };

    filterData = (inputValue) => {
        return this.state.data
            .map(option => ({
                "label": option.title,
                "value": option.title,
                "properties": option.properties,
                "uid": option.uid,
                "parentRef": option.parentRef
            }))
            .filter(i =>
                i.label.toLowerCase().includes(inputValue.toLowerCase())
            );
    };


    render() {
        const { classes, theme } = this.props;

        const selectStyles = {
            valueContainer: styles => ({
                ...styles,
                margin: '20px'
            }),
            dropdownIndicator: (styles, { isFocused }) => ({
                ...styles,
                padding: '0px',
                transition: 'all .0s ease',
                transform: isFocused ? 'rotate(180deg)' : null
            }),
            input: base => ({
                ...base,
                color: theme.palette.text.primary,
                '& input': {
                    font: 'inherit',
                },
            }),
            loadingMessage: styles => ({
                ...styles,
                color: 'black',
                textAlign: 'left'
            }),
            option: (styles, { data }) => {
                return {
                    ...styles,
                    backgroundImage: data.properties['picture:views'].length > 4 ? `url(${ConfigurationManager.getProperty('NUXEO_HOST')}/nuxeo/nxfile/default/${data
                        .uid}/picture:views/0/content/${data
                            .properties['picture:views'][4]
                            .filename})` : "",
                    backgroundPosition: "1em",
                    padding: "2em 8em",
                    backgroundColor: "white",
                    float: "left",
                    border: "solid 1px #e3e3e3",
                    backgroundSize: "auto",
                    backgroundRepeat: "no-repeat",
                    fontSize: "Large",
                    fontWeight: "Bold",
                    "&::after": {
                        content: `'\\A ${data.properties['public:styleGuideName']}'`,
                        fontSize: "small",
                        whiteSpace: "pre",
                        fontWeight: "normal"
                    },
                    "&:hover": {
                        backgroundColor: "#fefefe",
                        cursor: "pointer",
                        boxShadow: "inset 10px 10px 66px -44px rgba(0,0,0,0.19)",
                    },
                };
            }
        }

        return (
            <div className={classes.root} >
                <NoSsr>
                    <AsyncSelect
                        isMulti
                        classes={classes}
                        styles={selectStyles}
                        components={losComponentes}
                        defaultOptions={
                            this.props.data
                                .map(option => ({
                                    "label": option.title,
                                    "value": option.title,
                                    "properties": option.properties,
                                    "uid": option.uid,
                                    "parentRef": option.parentRef
                                }))
                        }
                        value={
                            this.props.value === '' || this.props.value === null ?
                                [] :
                                Array.isArray(this.props.value) ?
                                    this.props.value
                                        .map(option => ({
                                            label: option,
                                            value: option
                                        })) :
                                    { label: this.props.value, value: this.props.value }
                        }
                        loadOptions={this.loadOptions}
                        onChange={
                            this.props.type === 'pickupFile' ?
                                this.props.handlePickUps :
                                this.props.handleChange
                        }
                        onInputChange={(i) => this.props.loadData(i)}
                        placeholder=""
                        isClearable
                    />
                </NoSsr>
            </div>
        );
    }
}

Dropdown.propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(Dropdown);
